import { Link, useNavigate } from "@tanstack/react-router";
import { Briefcase, GitCompareArrows, Plus } from "lucide-react";
import { useState } from "react";
import { RoleCards } from "@/components/roles/RoleCards";
import { RoleFormDialog } from "@/components/roles/RoleFormDialog";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterRow } from "@/components/shared/filter-bar";
import { FilterPanel, selectedLabels } from "@/components/shared/filter-panel";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { ViewToggle } from "@/components/shared/view-toggle";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { useCursorList } from "@/hooks/use-cursor-list";
import { useEntityOptionLabels } from "@/hooks/use-entity-options";
import { useRoles } from "@/hooks/use-roles";
import { useViewMode } from "@/hooks/use-view-mode";
import { useSession } from "@/lib/auth-client";
import { carryListSearch } from "@/lib/list-search";
import { canEditAll } from "@/lib/permissions";

const LIMIT = 50;

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

export function RolesTable() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const isAdmin = canEditAll(session?.user?.role);
  const {
    search,
    setSearch,
    debouncedSearch,
    sort,
    onSort,
    filters,
    setFilter,
    resetFilters,
    pageRequest,
    pagination,
  } = useCursorList({
    sortKey: "name",
    defaultFilters: {
      statuses: ["active"] as string[],
      departmentId: null as string | null,
      roleTeamId: null as string | null,
      // "only" when showing only unfilled roles (deep-linked from Org Health).
      // A word, not "1", so the router's search serializer keeps it verbatim
      // in the URL rather than JSON-quoting a numeric-looking string.
      vacant: null as string | null,
    },
  });
  const { statuses, departmentId, roleTeamId, vacant } = filters;
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useViewMode("roles.view");

  const { data, isLoading, isError, error, refetch } = useRoles(
    {
      search: debouncedSearch,
      statuses,
      departmentId: departmentId ?? "",
      roleTeamId: roleTeamId ?? "",
      vacant: vacant === "only",
      sort: sort.key,
      dir: sort.dir,
    },
    pageRequest,
  );

  const rows = data?.data ?? [];

  // Resolve the selected entity ids to names so the collapsed summary shows
  // "Department: Engineering" rather than a bare filter name. Cached 5 min by
  // the options hook, so this costs nothing after the pick.
  const departmentLabels = useEntityOptionLabels("departments", departmentId ? [departmentId] : []);
  const roleTeamLabels = useEntityOptionLabels("role-teams", roleTeamId ? [roleTeamId] : []);
  const departmentName = departmentLabels.data?.[0]?.label;
  const roleTeamName = roleTeamLabels.data?.[0]?.label;

  const filterSummary = [
    ...(statuses.length ? [`Status: ${selectedLabels(STATUS_OPTIONS, statuses)}`] : []),
    ...(departmentId ? [`Department: ${departmentName ?? "…"}`] : []),
    ...(roleTeamId ? [`Role team: ${roleTeamName ?? "…"}`] : []),
    ...(vacant === "only" ? ["Unfilled only"] : []),
  ];
  const activeCount =
    statuses.length + (departmentId ? 1 : 0) + (roleTeamId ? 1 : 0) + (vacant === "only" ? 1 : 0);
  const hasActiveFilters = activeCount > 0 || search.length > 0;

  // Reset restores the list's defaults (Status → Active), not an empty set.
  const clearFilters = resetFilters;

  const controls = (
    <>
      <FilterPanel
        activeCount={activeCount}
        summary={filterSummary}
        onReset={clearFilters}
        canReset={hasActiveFilters}
      >
        <FilterRow label="Status">
          <FilterToggleGroup
            options={STATUS_OPTIONS}
            value={statuses}
            onChange={(v) => setFilter("statuses", v)}
          />
        </FilterRow>
        <FilterRow label="Department">
          <EntityCombobox
            entity="departments"
            value={departmentId}
            onChange={(v) => {
              setFilter("departmentId", v);
              setFilter("roleTeamId", null);
            }}
            includeNone
            noneLabel="Any department"
            placeholder="Any department"
            className="w-[220px]"
          />
        </FilterRow>
        <FilterRow label="Role team">
          <EntityCombobox
            entity="role-teams"
            value={roleTeamId}
            filters={departmentId ? { departmentId } : undefined}
            onChange={(v) => setFilter("roleTeamId", v)}
            includeNone
            noneLabel="Any team"
            placeholder="Any team"
            className="w-[220px]"
          />
        </FilterRow>
        <FilterRow label="Vacancy">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="roles-vacant-filter"
              checked={vacant === "only"}
              onCheckedChange={(c) => setFilter("vacant", c === true ? "only" : null)}
            />
            <label htmlFor="roles-vacant-filter">Unfilled roles only</label>
          </div>
        </FilterRow>
      </FilterPanel>

      <DataToolbar
        search={
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search roles…"
            className="flex-1 min-w-[200px] max-w-sm"
          />
        }
        actions={
          <>
            <ViewToggle value={view} onChange={setView} />
            {isAdmin && (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add role
              </Button>
            )}
          </>
        }
      />
    </>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {controls}
        <ErrorState message={error?.message ?? "Failed to load roles"} onRetry={() => refetch()} />
        {isAdmin && <RoleFormDialog open={createOpen} onOpenChange={setCreateOpen} />}
      </div>
    );
  }

  const emptyState = (
    <EmptyState
      icon={Briefcase}
      title="No roles found"
      description={
        hasActiveFilters
          ? "No roles match your search and filters."
          : isAdmin
            ? "Define your first role to get started."
            : "There are no roles to show."
      }
      action={
        hasActiveFilters ? (
          <Button variant="outline" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : isAdmin ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add role
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <div className="space-y-4">
      {controls}

      {view === "card" ? (
        <RoleCards roles={rows} loading={isLoading} empty={emptyState} />
      ) : (
        <DataTable
          headers={[
            { label: "Name", sortKey: "name" },
            { label: "Department", sortKey: "departmentName", className: "hidden sm:table-cell" },
            { label: "Status", sortKey: "status" },
            { label: "" },
          ]}
          loading={isLoading}
          sort={sort}
          onSort={onSort}
          empty={emptyState}
        >
          {rows.map((r) => (
            <tr
              key={r.id}
              {...interactiveRowProps(
                () =>
                  navigate({
                    to: "/dashboard/roles/$roleId",
                    params: { roleId: r.id },
                    search: carryListSearch(),
                  }),
                `Open ${r.name}`,
              )}
            >
              <td className="px-4 py-2.5 font-medium text-foreground">{r.name}</td>
              <td className="hidden sm:table-cell px-4 py-2.5 text-muted-foreground">
                {r.departmentName || "—"}
              </td>
              <td className="px-4 py-2.5">
                <LifecycleBadge status={r.status} />
              </td>
              <td className="w-px whitespace-nowrap px-4 py-2.5 text-right">
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                >
                  {/* stopPropagation so the row's own navigate-to-detail doesn't fire */}
                  <Link
                    to="/dashboard/roles/compare"
                    search={{ targetRole: r.id }}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Compare your role with ${r.name}`}
                  >
                    <GitCompareArrows className="mr-1 size-3.5" />
                    Compare
                  </Link>
                </Button>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <CursorPagination {...pagination(data)} limit={LIMIT} loading={isLoading} itemLabel="roles" />

      {isAdmin && <RoleFormDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </div>
  );
}
